// Focus-trap action porting the landing's proven semantics (getFocusable + Tab wrap
// + Escape + focus restore, index.html:1034). Also marks the rest of the document
// `inert` so AT and keyboard users can't reach the background while a modal is open.

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute("hidden") && el.offsetParent !== null
  );
}

export interface TrapParams {
  onEscape?: (() => void) | undefined;
  /** Element to focus first; defaults to the first focusable descendant. */
  initial?: HTMLElement | null | undefined;
}

export function focusTrap(node: HTMLElement, params: TrapParams = {}) {
  let current = params;
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const inerted: Element[] = [];

  // Inert every sibling at each ancestor level from the modal up to <body>, so the
  // entire document EXCEPT the modal's own subtree becomes inert — the portal-free
  // way to guarantee background content is unreachable by keyboard and AT.
  let ancestor: HTMLElement | null = node;
  while (ancestor && ancestor !== document.body) {
    const parentEl: HTMLElement | null = ancestor.parentElement;
    if (parentEl) {
      const siblings: Element[] = Array.from(parentEl.children);
      for (const sibling of siblings) {
        // Global announcement surfaces (LiveRegion, Toaster) must stay in the
        // accessibility tree while a modal is open, or SR announcements go silent.
        if (sibling.hasAttribute("data-focus-trap-exempt")) continue;
        if (sibling !== ancestor && !sibling.hasAttribute("inert")) {
          sibling.setAttribute("inert", "");
          inerted.push(sibling);
        }
      }
    }
    ancestor = parentEl;
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      current.onEscape?.();
      return;
    }
    if (event.key !== "Tab") return;
    const items = focusable(node);
    if (!items.length) {
      event.preventDefault();
      node.focus();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  node.addEventListener("keydown", onKeydown);
  // Defer initial focus so the element is laid out.
  queueMicrotask(() => {
    const items = focusable(node);
    // A modal's dismiss affordance is first in DOM order but the last thing the user
    // came for: landing on "Close" makes a form read as if it had nothing in it. An
    // element marked `data-focus-skip` is passed over for the INITIAL landing only —
    // Tab order below is untouched, so the control stays reachable in DOM order.
    const target =
      current.initial ?? items.find((el) => !el.hasAttribute("data-focus-skip")) ?? items[0] ?? node;
    target.focus();
  });

  return {
    update(next: TrapParams) {
      current = next;
    },
    destroy() {
      node.removeEventListener("keydown", onKeydown);
      for (const el of inerted) el.removeAttribute("inert");
      previouslyFocused?.focus?.();
    }
  };
}
